/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { lazy, Suspense } from 'react';

import { jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';

import UFOSegment from '@atlaskit/react-ufo/segment';

import { useScrollDepthEvent } from '../../../common/analytics/hooks/useScrollDepthEvent';
import { useTierShownEvent } from '../../../common/analytics/hooks/useTierShownEvent';
import { SessionTracker } from '../../../common/analytics/SessionTracker';
import { MicrositeTier, TierResolution } from '../../../common/analytics/types';
import { useScrollToHash } from '../../../common/deep-link/useScrollToHash';
import { SentryErrorBoundary } from '../../../common/sentry';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { LoadingView } from '../../common/ui/LoadingView';
import { PageErrorView } from '../../common/ui/PageErrorView';
import { forceLoggedOutViewAtom } from '../../state';

/**
 * Lazy-loaded gating component that resolves the user's `cloudId` and primes
 * the shared atoms before the logged-in view mounts. Code-split out so the
 * App Switcher / cloudId resolution logic and the heavy LoggedInView modules
 * are only fetched once we know the user is authenticated.
 */
const LoggedInGate = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlassian/twg-microsite-experience_logged-in-gate" */ './LoggedInGate'
		),
);

/**
 * Lazy-loaded marketing/logged-out experience. Code-split out so the
 * landing-page assets are only fetched when the visitor is unauthenticated
 * (or has toggled the dev "Logged Out View" switcher).
 */
const LoggedOutView = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite-experience_logged-out-view" */ './views/LoggedOutView'
	).then((m) => ({ default: m.LoggedOutView })),
);

export function Home(): React.JSX.Element | null {
	const { loading, isAuthed: rawIsAuthed } = useAtlassianUserContext();

	// Debug Panel override — when the developer toggles "Switch to logged
	// out view", we treat the session as unauthenticated for the rest of
	// the render tree even if the user is in fact signed in. This is
	// purely a dev-only affordance and is gated by the same feature
	// flag that controls the Debug Panel itself.
	const forceLoggedOutView = useAtomValue(forceLoggedOutViewAtom);
	const isAuthed = rawIsAuthed && !forceLoggedOutView;

	// Fire `track.twgMicrositeTier.shown` for the logged-out tier as
	// soon as we know the user is not authenticated. The hook safely
	// no-ops when called with `tier === undefined` (loading / blocked /
	// authed branches), so we can call it unconditionally at the top
	// without violating Rules of Hooks.
	useTierShownEvent({
		tier: !loading && !isAuthed ? MicrositeTier.LoggedOut : undefined,
		resolution: TierResolution.Organic,
		hasUserMetrics: false,
		hasOrgMetrics: false,
		metricCount: 0,
	});

	// Track scroll-depth across the entire home shell. The hook is mounted
	// here (top-level Home component) so the same instance covers both the
	// logged-out and logged-in views — once a bucket fires for the current
	// session it stays fired even if the user signs in mid-scroll.
	useScrollDepthEvent();

	// Deep-link: scroll to #cli / #mcp / #getting-started when the
	// anchor-scrolling gate is on — on first load and on in-page hashchange.
	// Mounted here so it covers both the logged-out and logged-in views; the
	// hook waits for the (lazy) target section and no-ops when the gate is off
	// or the hash is unknown.
	useScrollToHash();

	if (loading) {
		return (
			<UFOSegment name="twg-microsite.home">
				<LoadingView holdName="twg-microsite.home.user-context-loading" appearance="shell" />
			</UFOSegment>
		);
	}

	if (!isAuthed) {
		return (
			<UFOSegment name="twg-microsite.home">
				<SessionTracker authState="loggedOut" tier={MicrositeTier.LoggedOut} />
				<UFOSegment name="twg-microsite.home.logged-out">
					<Suspense
						fallback={
							<LoadingView holdName="twg-microsite.home.user-context-loading" appearance="shell" />
						}
					>
						<LoggedOutView />
					</Suspense>
				</UFOSegment>
			</UFOSegment>
		);
	}

	return (
		<UFOSegment name="twg-microsite.home">
			<SessionTracker authState="loggedIn" />
			<UFOSegment name="twg-microsite.home.logged-in">
				<Suspense fallback={<LoadingView holdName="twg-microsite.home.user-context-loading" />}>
					<LoggedInGate />
				</Suspense>
			</UFOSegment>
		</UFOSegment>
	);
}

/**
 * Top-level wrapper that catches any unhandled render error from the Home
 * experience and renders the `ErrorView` fallback. This is what consumers
 * import as the default export — `Home` itself remains available as a named
 * export for tests or for nested usage that does not need the boundary.
 */
export default function HomeWithErrorBoundary(): React.JSX.Element {
	return (
		<SentryErrorBoundary fallback={<PageErrorView />} loggerName="home.errorBoundary">
			<Home />
		</SentryErrorBoundary>
	);
}
